/**
 * Puzzle Image Resolver
 * Handles dynamic image selection and metadata resolution
 */

import { PUZZLE_IMAGE_PATHS, PUZZLE_METADATA_CONFIG } from '../constants/puzzle'

/**
 * Get daily puzzle image based on day of month (Argentina timezone)
 * Called at runtime to support PWA scenarios where app stays open past midnight
 *
 * @returns {{ id: string, src: string }} Image identifier and full path
 * @example
 * const { id, src } = getDailyImage()
 * // Day 15: { id: 'd15', src: '/puzzle/daily/d15.webp' }
 */
export function getDailyImage () {
  try {
    // Get current day in Argentina timezone (01-31)
    const day = new Date().toLocaleString('en-US', {
      timeZone: 'America/Argentina/Buenos_Aires',
      day: '2-digit'
    })

    const imageId = `d${day}`

    return {
      id: imageId,
      src: `${PUZZLE_IMAGE_PATHS.dailyBase}/${imageId}.webp`
    }
  } catch (error) {
    // Fallback if timezone API fails (e.g., old browsers or private mode)
    console.warn('[PuzzleImageResolver] Timezone detection failed, using cover')
    return getCoverImage()
  }
}

/**
 * Get image metadata with dynamic key generation
 * Validation ensures only known image IDs generate keys:
 * - Daily: d01-d31 (zero-padded day numbers)
 * - Static: cover, fallback
 * - Unknown: Falls back to 'default'
 *
 * @param {string} imageId - Image identifier (e.g., 'd15', 'cover')
 * @returns {object} Metadata object with i18n keys
 *
 * @example
 * getImageMetadata('d15')
 * // { descriptionKey: 'puzzle.images.d15' }
 *
 * getImageMetadata('cover')
 * // { descriptionKey: 'puzzle.images.cover' }
 *
 * getImageMetadata('invalid')
 * // { descriptionKey: 'puzzle.images.default' }
 */
export function getImageMetadata (imageId) {
  const { imageDescriptionNamespace, fallbackKey } = PUZZLE_METADATA_CONFIG

  // Validate imageId format
  // Daily: d01-d31 (matches day of month with zero padding)
  const isValidDaily = /^d(0[1-9]|[12][0-9]|3[01])$/.test(imageId)
  // Static: known fallback images
  const isKnownStatic = ['cover', 'fallback'].includes(imageId)

  // Use validated key or fall back to default
  const key = (isValidDaily || isKnownStatic)
    ? imageId
    : fallbackKey

  return {
    descriptionKey: `${imageDescriptionNamespace}.${key}`
    // Phase 2: Add more keys here
    // altKey: `puzzle.alt.${key}`,
    // creditKey: `puzzle.credits.${key}`,
    // locationKey: `puzzle.locations.${key}`
  }
}

/**
 * Get cover image (always available fallback)
 * @returns {{ id: string, src: string }}
 */
export function getCoverImage () {
  return {
    id: 'cover',
    src: PUZZLE_IMAGE_PATHS.cover
  }
}

/**
 * Get fallback image
 * @returns {{ id: string, src: string }}
 */
export function getFallbackImage () {
  return {
    id: 'fallback',
    src: PUZZLE_IMAGE_PATHS.fallback
  }
}
